import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle } from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { StatusBadge } from '../components/UI/StatusBadge';
import { CountBadge } from '../components/UI/CountBadge';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import SelfServiceWorklistSkeleton from '../components/UI/SelfServiceWorklistSkeleton';
import ConfirmModal from '../components/UI/ConfirmModal';
import ProcessProgressRail from '../components/UI/ProcessProgressRail';
import EventTimeline from '../components/UI/EventTimeline';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  useWorkPassCapabilities,
  useWorkPassQueue,
  useWorkPassDecision,
  useRecommendWorkPass,
  useApproveWorkPass,
  useReturnWorkPass,
} from '../hooks/useWorkPassDecisions';
import WorkPassNotificationsPausedBanner from '../components/workPass/WorkPassNotificationsPausedBanner';
import { formatDate } from '../utils/dateUtils';

const TABS = [
  { id: 'my_actions', label: 'My actions' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'history', label: 'History' },
];

const EVENT_LABELS = {
  created: 'Cycle opened',
  recommended: 'Recommendation recorded',
  approved: 'Department approved',
  returned: 'Returned for a new recommendation',
  configuration_exception: 'Configuration exception',
  exception_resolved: 'Exception resolved',
  renewal_created: 'Renewal work created',
  renewal_updated: 'Renewal updated',
  renewed: 'Pass renewed',
  returned_from_renewal: 'Returned from renewal',
  terminated_promoted: 'Termination promoted',
  superseded: 'Superseded',
  locked_for_reconciliation: 'Locked for HR reconciliation',
};

const eventColor = (type) => {
  if (['approved', 'renewed'].includes(type)) return 'green';
  if (['returned', 'configuration_exception', 'locked_for_reconciliation'].includes(type)) return 'orange';
  if (type === 'superseded') return 'gray';
  return 'blue';
};

const SelfServiceWorkPassDecisions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id') || '';
  const tab = searchParams.get('tab') || 'my_actions';
  const isCompact = useIsMobile(1024);
  const worklistRef = useRef(null);
  const listPaneRef = useRef(null);
  const detailPaneRef = useRef(null);
  const [rationale, setRationale] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [queueHydrated, setQueueHydrated] = useState(false);

  const { data: caps } = useWorkPassCapabilities();
  const { data: queue = [], isLoading: queueLoading, isError, refetch } = useWorkPassQueue(tab);
  const { data: detail, isLoading: detailLoading } = useWorkPassDecision(selectedId);
  const recommend = useRecommendWorkPass();
  const approve = useApproveWorkPass();
  const returnDecision = useReturnWorkPass();

  const counts = useMemo(() => ({
    my_actions: caps?.pending_work_pass_actions || 0,
    upcoming: caps?.upcoming_work_pass || 0,
  }), [caps]);

  const openDetail = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('id', id);
    setSearchParams(next);
  };

  const closeCompactDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next);
  };

  const changeTab = (nextTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', nextTab);
    next.delete('id');
    setSearchParams(next);
    setRationale('');
    setReturnReason('');
  };

  useEffect(() => {
    if (!queueLoading) {
      setQueueHydrated(true);
    }
  }, [queueLoading]);

  useEffect(() => {
    if (isCompact || !selectedId || queueLoading) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const pane = listPaneRef.current;
      const card = pane?.querySelector('[data-selected="true"]');
      if (!pane || !card) return;
      const paneRect = pane.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      if (cardRect.top < paneRect.top) {
        pane.scrollTop += cardRect.top - paneRect.top;
      } else if (cardRect.bottom > paneRect.bottom) {
        pane.scrollTop += cardRect.bottom - paneRect.bottom;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isCompact, selectedId, queueLoading]);

  useEffect(() => {
    if (isCompact || !selectedId) return;
    detailPaneRef.current?.scrollTo({ top: 0 });
  }, [isCompact, selectedId]);

  const pendingWith = detail?.progress?.find((step) => step.state === 'current')?.subtitle || '';
  const actions = detail?.allowed_actions || [];

  const commitRecommend = (outcome) => {
    recommend.mutate(
      { id: selectedId, data: { outcome, rationale } },
      { onSuccess: () => { setConfirm(null); setRationale(''); } },
    );
  };

  return (
    <DashboardLayout>
      <div className="bg-gray-50 flex flex-col gap-6 lg:h-[calc(100dvh-7.5rem)] lg:min-h-0 lg:overflow-hidden">
        <div className="flex-shrink-0">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-blue-800" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Work Pass Decisions</h1>
          </div>
          <p className="text-sm text-gray-500 ml-12">
            Recommend retain or end for workers entering the 90-day expiry window
          </p>
          <div className="mt-3">
            <WorkPassNotificationsPausedBanner paused={!!caps?.notifications_paused} />
          </div>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide flex-shrink-0">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => changeTab(item.id)}
              className={`tab-btn ${tab === item.id ? 'active' : ''}`}
            >
              {item.label}
              {counts[item.id] > 0 && (
                <CountBadge count={counts[item.id]} theme="warning" active={tab === item.id} />
              )}
            </button>
          ))}
        </div>

        {queueLoading && !queueHydrated ? (
          <div className="lg:flex-1 lg:min-h-0">
            <SelfServiceWorklistSkeleton />
          </div>
        ) : queueLoading ? (
          <div className="flex items-center justify-center py-12 lg:flex-1">
            <LoadingSpinner size="lg" />
          </div>
        ) : isError ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center lg:flex-1">
            <AlertTriangle className="w-8 h-8 text-orange-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-800">Could not load this queue</p>
            <button type="button" className="btn btn-primary mt-3" onClick={() => refetch()}>
              Try again
            </button>
          </div>
        ) : (
          <div
            ref={worklistRef}
            className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 lg:gap-6 lg:min-h-0 lg:flex-1 lg:overflow-hidden"
          >
            <div
              ref={listPaneRef}
              className={`${
                isCompact && selectedId ? 'hidden' : 'space-y-3'
              } lg:min-h-0 lg:overflow-y-auto lg:overscroll-y-contain lg:pr-1`}
            >
              {queue.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
                  <Shield className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-900 font-medium">
                    {tab === 'my_actions'
                      ? 'No decisions need your action'
                      : tab === 'upcoming'
                        ? 'No other open cycles'
                        : 'No completed cycles yet'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                    {tab === 'my_actions'
                      ? 'A worker appears here only when it is your turn to recommend or approve. That is not the same as the Work Pass Expiry tracker.'
                      : tab === 'upcoming'
                        ? 'Open cycles that are waiting on someone else in your scope will show here.'
                        : 'Approved, completed, or superseded cycles from this portal will appear here.'}
                  </p>
                  {tab === 'my_actions' && caps?.is_hr && (
                    <Link
                      to="/admin/employee-lifecycle?tab=work-pass"
                      className="btn btn-outline mt-4"
                    >
                      Open HR work pass board
                    </Link>
                  )}
                </div>
              ) : (
                queue.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    data-selected={selectedId === item.id ? 'true' : undefined}
                    onClick={() => openDetail(item.id)}
                    className={`w-full text-left bg-white rounded-2xl border p-4 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                      selectedId === item.id
                        ? 'border-blue-800 shadow-md'
                        : 'border-gray-200 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {item.worker_name_snapshot}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {item.worker_staff_id}
                          {item.trade_name ? ` · ${item.trade_name}` : ''}
                          {item.pass_type_label ? ` · ${item.pass_type_label}` : ''}
                        </p>
                      </div>
                      <StatusBadge status={item.status} />
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-500 tabular-nums">
                        Expires {formatDate(item.expiry_date)}
                      </p>
                      <p className={`text-xs font-medium tabular-nums ${
                        item.days_remaining <= 30 ? 'text-red-600' : 'text-gray-500'
                      }`}
                      >
                        {item.days_remaining} days left
                      </p>
                    </div>
                    {item.is_decision_overdue && (
                      <p className="text-xs text-orange-700 mt-2">Departmental decision is overdue</p>
                    )}
                  </button>
                ))
              )}
            </div>

            <div
              ref={detailPaneRef}
              className={`${
                isCompact && !selectedId ? 'hidden' : ''
              } lg:min-h-0 lg:h-full lg:overflow-y-auto lg:overscroll-y-contain lg:pr-1`}
            >
              {isCompact && selectedId && (
                <button type="button" onClick={closeCompactDetail} className="btn btn-outline mb-3">
                  <ArrowLeft className="w-4 h-4" />
                  Back to list
                </button>
              )}
              {!selectedId ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-500 lg:h-full lg:flex lg:flex-col lg:items-center lg:justify-center">
                  Select a worker to review the expiry facts
                </div>
              ) : detailLoading || !detail ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-8 flex justify-center lg:h-full lg:items-center">
                  <LoadingSpinner size="md" />
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold text-gray-900">
                        {detail.worker_name_snapshot}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {detail.request_number} · {detail.worker_staff_id}
                      </p>
                    </div>
                    <StatusBadge status={detail.status} />
                  </div>

                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-xs text-gray-500">Pass</dt>
                      <dd className="font-medium text-gray-900">{detail.pass_type_label || detail.pass_type}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Expiry</dt>
                      <dd className="font-medium text-gray-900 tabular-nums">{formatDate(detail.expiry_date)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Trade</dt>
                      <dd className="font-medium text-gray-900">{detail.trade_name || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Department</dt>
                      <dd className="font-medium text-gray-900">{detail.department_name || '—'}</dd>
                    </div>
                  </dl>

                  {detail.status === 'configuration_exception' && (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                      {detail.config_exception_reason || 'HR must resolve a missing or overlapping role before this cycle can continue.'}
                    </div>
                  )}
                  {detail.locked_for_reconciliation && (
                    <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800">
                      {detail.lock_reason || 'Locked for HR reconciliation with a departure request.'}
                    </div>
                  )}

                  <ProcessProgressRail
                    steps={detail.progress || []}
                    pendingWith={pendingWith}
                  />

                  {detail.recommendation_rationale && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Recommendation</p>
                      <p className="text-sm text-gray-800">
                        {detail.recommended_outcome === 'retain' ? 'Retain / renew' : 'End at expiry'}
                        {detail.recommended_by_name ? ` · ${detail.recommended_by_name}` : ''}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">{detail.recommendation_rationale}</p>
                    </div>
                  )}

                  {actions.includes('recommend') && (
                    <div className="space-y-3">
                      <label className="block">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Rationale</span>
                        <textarea
                          value={rationale}
                          onChange={(event) => setRationale(event.target.value)}
                          rows={3}
                          className="input mt-1 w-full"
                          placeholder="Why should this pass be retained or ended?"
                        />
                      </label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          type="button"
                          className="btn btn-primary flex-1"
                          disabled={!rationale.trim()}
                          onClick={() => setConfirm({ kind: 'retain' })}
                        >
                          Recommend retain
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger flex-1"
                          disabled={!rationale.trim()}
                          onClick={() => setConfirm({ kind: 'end' })}
                        >
                          Recommend end at expiry
                        </button>
                      </div>
                    </div>
                  )}

                  {actions.includes('approve') && (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        type="button"
                        className="btn btn-primary flex-1"
                        onClick={() => setConfirm({ kind: 'approve' })}
                      >
                        Approve recommendation
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline flex-1"
                        onClick={() => setConfirm({ kind: 'return' })}
                      >
                        Return
                      </button>
                    </div>
                  )}

                  {actions.includes('return') && !actions.includes('approve') && (
                    <button
                      type="button"
                      className="btn btn-outline w-full"
                      onClick={() => setConfirm({ kind: 'return' })}
                    >
                      Return
                    </button>
                  )}

                  <EventTimeline
                    events={(detail.events || []).map((event) => ({
                      id: event.id,
                      label: EVENT_LABELS[event.event_type] || event.event_type,
                      subtitle: [event.actor_name, event.reason].filter(Boolean).join(' · '),
                      timestamp: event.created_at,
                      color: eventColor(event.event_type),
                    }))}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirm?.kind === 'retain' || confirm?.kind === 'end'}
        theme="employee"
        variant={confirm?.kind === 'end' ? 'danger' : 'primary'}
        title={confirm?.kind === 'end' ? 'Recommend end at expiry' : 'Recommend retain'}
        confirmLabel={confirm?.kind === 'end' ? 'Confirm end at expiry' : 'Confirm retain'}
        isLoading={recommend.isPending}
        confirmDisabled={!rationale.trim()}
        onCancel={() => setConfirm(null)}
        onConfirm={() => commitRecommend(confirm.kind === 'end' ? 'end_at_expiry' : 'retain')}
      >
        <p className="text-sm text-gray-700">
          {detail?.worker_name_snapshot} · {detail?.pass_type_label} expires {formatDate(detail?.expiry_date)}.
        </p>
        <p className="text-sm text-gray-600 mt-2">
          {confirm?.kind === 'end'
            ? 'HOD or Deputy HOD will review. If they approve, HR receives a prefilled termination request.'
            : 'HOD or Deputy HOD will review. If they approve, HR tracks the pass renewal.'}
        </p>
      </ConfirmModal>

      <ConfirmModal
        isOpen={confirm?.kind === 'approve'}
        theme="employee"
        variant="primary"
        title="Approve this recommendation"
        confirmLabel="Approve"
        isLoading={approve.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => approve.mutate(
          { id: selectedId, data: {} },
          { onSuccess: () => setConfirm(null) },
        )}
      >
        <p className="text-sm text-gray-700">
          Approve {detail?.recommended_outcome === 'end_at_expiry' ? 'end at expiry' : 'retain / renew'} for {detail?.worker_name_snapshot}.
        </p>
      </ConfirmModal>

      <ConfirmModal
        isOpen={confirm?.kind === 'return'}
        theme="employee"
        variant="danger"
        title="Return for a new recommendation"
        confirmLabel="Return"
        isLoading={returnDecision.isPending}
        confirmDisabled={!returnReason.trim()}
        onCancel={() => setConfirm(null)}
        onConfirm={() => returnDecision.mutate(
          { id: selectedId, data: { reason: returnReason } },
          { onSuccess: () => { setConfirm(null); setReturnReason(''); } },
        )}
      >
        <label className="block">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Reason</span>
          <textarea
            value={returnReason}
            onChange={(event) => setReturnReason(event.target.value)}
            rows={3}
            className="input mt-1 w-full"
          />
        </label>
      </ConfirmModal>
    </DashboardLayout>
  );
};

export default SelfServiceWorkPassDecisions;
